import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

export default function TemplatePicker({ onSelectTemplate, onClose, projectId }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categories, setCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Template category metadata for display
    const categoryMetadata = {
        'with-tabs': { label: 'With Tabs', icon: 'fa-solid fa-table-columns', description: 'Multi-tab layouts for organized content' },
        'with-accordions': { label: 'With Accordions', icon: 'fa-solid fa-bars-staggered', description: 'Collapsible accordion sections' },
        'sequential': { label: 'Sequential', icon: 'fa-solid fa-list-ol', description: 'Step-by-step wizard flow' },
        'non-sequential': { label: 'Non-Sequential', icon: 'fa-solid fa-grip', description: 'All variables visible at once' },
        'hybrid': { label: 'Hybrid', icon: 'fa-solid fa-layer-group', description: 'Mixed tab and accordion layouts' },
        'minimal': { label: 'Minimal', icon: 'fa-solid fa-minus', description: 'Clean, simple single-column layout' },
        'comprehensive': { label: 'Comprehensive', icon: 'fa-solid fa-plus', description: 'Full-featured multi-page layouts' }
    };

    const normalizeTemplate = (template) => ({
        ...template,
        id: template.id || template.Id,
        name: template.name || template.Name,
        category: template.category || template.Category,
        description: template.description || template.Description,
        thumbnailUrl: template.thumbnailUrl || template.ThumbnailUrl,
        layoutSchema: template.layoutSchema || template.LayoutSchema,
        styleSchema: template.styleSchema || template.StyleSchema,
        isSystemTemplate: template.isSystemTemplate ?? template.IsSystemTemplate,
        createdAt: template.createdAt || template.CreatedAt
    });

    const loadTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch all templates
            const response = await apiFetch('/api/templates');
            if (response.ok) {
                let data = await response.json();
                if (Array.isArray(data) && data.length === 0) {
                    const seedResponse = await apiFetch('/api/templates/seed', { method: 'POST' });
                    if (seedResponse.ok) {
                        const seedResult = await seedResponse.json();
                        data = seedResult.templates || seedResult.Templates || [];
                    }
                }

                const normalizedTemplates = Array.isArray(data) ? data.map(normalizeTemplate) : [];
                setTemplates(normalizedTemplates);
                
                // Extract unique categories
                const uniqueCategories = [...new Set(normalizedTemplates.map(t => t.category).filter(Boolean))].sort();
                setCategories(uniqueCategories);
            } else {
                throw new Error('Failed to load templates');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error loading templates:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await apiFetch('/api/templates/categories');
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    };

    useEffect(() => {
        loadTemplates();
        loadCategories();
    }, []);

    const filteredTemplates = templates.filter(t => {
        const matchesCategory = selectedCategory ? t.category === selectedCategory : true;
        const matchesSearch = searchQuery 
            ? t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (t.description || '').toLowerCase().includes(searchQuery.toLowerCase())
            : true;
        return matchesCategory && matchesSearch;
    });

    const handleSelectTemplate = async (template) => {
        try {
            // Apply the template to the current project
            const payload = {
                layoutSchema: template.layoutSchema,
                styleSchema: template.styleSchema
            };
            
            const response = await apiFetch(`/api/projects/${projectId}/apply-template`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                // Notify parent that template was applied
                if (onSelectTemplate) {
                    onSelectTemplate(template);
                }
            } else {
                throw new Error('Failed to apply template');
            }
        } catch (err) {
            console.error('Error applying template:', err);
            setError(err.message);
        }
    };

    const handleStartFromScratch = () => {
        if (onSelectTemplate) {
            onSelectTemplate(null); // null indicates start from scratch
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem'
        }} onClick={onClose}>
            <div style={{
                background: 'var(--bg-primary, #ffffff)',
                borderRadius: '12px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
                width: '100%',
                maxWidth: '1000px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    borderBottom: '1px solid var(--border, #e2e8f0)',
                    background: 'var(--bg-secondary, #f8fafc)'
                }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        <i className="fa-solid fa-layer-group" style={{ marginRight: '0.5rem', color: 'var(--text-secondary)' }}></i>
                        Select a Template
                    </h2>
                    <button 
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)',
                            padding: '0.25rem'
                        }}
                        title="Close"
                    >
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>

                {/* Sub-header with search and category filter */}
                <div style={{
                    padding: '1rem 1.5rem',
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        <input
                            type="text"
                            placeholder="Search templates..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.6rem 1rem',
                                border: '1px solid var(--border)',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                background: 'var(--bg-primary)',
                                color: 'var(--text-primary)'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button 
                            onClick={() => setSelectedCategory(null)}
                            style={{
                                padding: '0.5rem 1rem',
                                border: 'none',
                                background: selectedCategory === null ? 'var(--text-primary)' : 'var(--bg-secondary)',
                                color: selectedCategory === null ? 'white' : 'var(--text-primary)',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            All Categories
                        </button>
                        {categories.map(cat => (
                            <button 
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                style={{
                                    padding: '0.5rem 1rem',
                                    border: 'none',
                                    background: selectedCategory === cat ? 'var(--text-primary)' : 'var(--bg-secondary)',
                                    color: selectedCategory === cat ? 'white' : 'var(--text-primary)',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                                title={categoryMetadata[cat]?.description || cat}
                            >
                                {categoryMetadata[cat]?.label || cat}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '1rem 1.5rem'
                }}>
                    {loading ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '300px',
                            color: 'var(--text-secondary)'
                        }}>
                            <div className="loader"></div>
                        </div>
                    ) : error ? (
                        <div style={{
                            padding: '2rem',
                            background: 'var(--bg-secondary)',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            color: 'var(--text-error, #ef4444)',
                            textAlign: 'center'
                        }}>
                            <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                            <p>Failed to load templates: {error}</p>
                            <button 
                                onClick={loadTemplates}
                                style={{
                                    marginTop: '1rem',
                                    padding: '0.5rem 1rem',
                                    background: 'var(--text-primary)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                <i className="fa-solid fa-refresh" style={{ marginRight: '0.5rem' }}></i> Retry
                            </button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* Start from scratch option */}
                            <div 
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '1.5rem',
                                    border: '2px dashed var(--border)',
                                    borderRadius: '12px',
                                    background: 'var(--bg-secondary)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    ':hover': {
                                        borderColor: 'var(--text-primary)',
                                        background: 'var(--bg-primary)'
                                    }
                                }}
                                onClick={handleStartFromScratch}
                            >
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'var(--bg-primary)',
                                    borderRadius: '8px',
                                    marginRight: '1.5rem',
                                    fontSize: '2rem',
                                    color: 'var(--text-secondary)'
                                }}>
                                    <i className="fa-solid fa-file"></i>
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                                        Start from Scratch
                                    </h3>
                                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                        Create a completely custom layout with no predefined structure
                                    </p>
                                </div>
                            </div>

                            {/* Templates grid */}
                            {filteredTemplates.length > 0 ? (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '1.5rem'
                                }}>
                                    {filteredTemplates.map(template => (
                                        <div 
                                            key={template.id}
                                            style={{
                                                border: '1px solid var(--border)',
                                                borderRadius: '12px',
                                                padding: '1.25rem',
                                                background: 'var(--bg-primary)',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                ':hover': {
                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                            onClick={() => handleSelectTemplate(template)}
                                        >
                                            {/* Template header */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: '1rem'
                                            }}>
                                                <div>
                                                    <h3 style={{
                                                        margin: 0,
                                                        fontSize: '1rem',
                                                        fontWeight: 600,
                                                        color: 'var(--text-primary)',
                                                        lineHeight: 1.4
                                                    }}>
                                                        {template.name}
                                                    </h3>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        marginTop: '0.25rem',
                                                        padding: '0.2rem 0.5rem',
                                                        background: 'var(--bg-secondary)',
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                        color: 'var(--text-secondary)'
                                                    }}>
                                                        {categoryMetadata[template.category]?.label || template.category}
                                                    </span>
                                                </div>
                                                {template.isSystemTemplate && (
                                                    <span 
                                                        style={{
                                                            padding: '0.2rem 0.5rem',
                                                            background: 'var(--text-primary)',
                                                            color: 'white',
                                                            borderRadius: '4px',
                                                            fontSize: '0.65rem',
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em'
                                                        }}
                                                        title="System Template"
                                                    >
                                                        <i className="fa-solid fa-star" style={{ marginRight: '0.25rem' }}></i>System
                                                    </span>
                                                )}
                                            </div>

                                            {/* Template description */}
                                            <p style={{
                                                margin: '0 0 1rem 0',
                                                fontSize: '0.85rem',
                                                color: 'var(--text-secondary)',
                                                lineHeight: 1.5
                                            }}>
                                                {template.description || 'No description available'}
                                            </p>

                                            {/* Template preview placeholder */}
                                            <div style={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: 'var(--bg-secondary)',
                                                borderRadius: '8px',
                                                padding: '2rem',
                                                margin: '1rem 0',
                                                minHeight: '140px',
                                                color: 'var(--text-secondary)',
                                                fontStyle: 'italic'
                                            }}>
                                                {template.thumbnailUrl ? (
                                                    <img 
                                                        src={template.thumbnailUrl}
                                                        alt={template.name}
                                                        style={{
                                                            maxWidth: '100%',
                                                            maxHeight: '140px',
                                                            borderRadius: '4px',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <i className="fa-solid fa-image" style={{ 
                                                            fontSize: '2.5rem', 
                                                            marginBottom: '0.5rem',
                                                            opacity: 0.5
                                                        }}></i>
                                                        <div style={{ fontSize: '0.8rem' }}>No preview available</div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Template metadata */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                paddingTop: '1rem',
                                                borderTop: '1px solid var(--border)',
                                                fontSize: '0.75rem',
                                                color: 'var(--text-secondary)'
                                            }}>
                                                <span>
                                                    <i className="fa-solid fa-calendar" style={{ marginRight: '0.35rem' }}></i>
                                                    Created {new Date(template.createdAt).toLocaleDateString()}
                                                </span>
                                                <span 
                                                    style={{
                                                        fontWeight: 600,
                                                        color: 'var(--text-primary)',
                                                        fontSize: '0.8rem'
                                                    }}
                                                >
                                                    Select <i className="fa-solid fa-arrow-right" style={{ marginLeft: '0.25rem' }}></i>
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '3rem',
                                    color: 'var(--text-secondary)',
                                    background: 'var(--bg-secondary)',
                                    borderRadius: '12px',
                                    border: '2px dashed var(--border)'
                                }}>
                                    <i className="fa-solid fa-search" style={{ 
                                        fontSize: '3rem', 
                                        marginBottom: '1rem',
                                        opacity: 0.5
                                    }}></i>
                                    <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                                        No templates found
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '0.85rem' }}>
                                        {selectedCategory 
                                            ? `No templates in the "${selectedCategory}" category`
                                            : searchQuery
                                                ? `No templates match "${searchQuery}"`
                                                : 'There are no templates available'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '1rem 1.5rem',
                    borderTop: '1px solid var(--border)',
                    background: 'var(--bg-secondary)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem'
                }}>
                    <button 
                        onClick={onClose}
                        style={{
                            padding: '0.6rem 1.5rem',
                            background: 'var(--bg-primary)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 600
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
